/**
 * Release Agent Auto-Open
 *
 * @module features/releases/releaseAgentAutoOpen
 * @description Pure helpers deciding when a release run's live agent should
 * auto-open the assistant panel onto a "watch it" kickoff thread, and
 * building that kickoff request. No React, no DOM — safe to unit test. See
 * `hooks/useReleaseAgentAutoOpen.ts` for the effect that drives this against
 * the live agent status and `AssistantContext`.
 */

import type { KickoffRequest } from '../assistant/onboardingFlow';
import { isLiveAgentStatus, type ReleaseAgentStatus } from './releaseTypes';

export const RELEASE_AGENT_AUTO_OPEN_KEY = 'chatsdet_release_agent_auto_open';

/** Minimal storage shape — satisfied by `window.sessionStorage` (once per run per browser session — deliberately not `localStorage`) and easy to fake in tests. */
export type KeyValueStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

/**
 * Auto-open exactly once per run: only while the agent is live (running,
 * sleeping, or waiting for human input) AND this run hasn't already been
 * auto-opened this session. A `null` storage (private window, blocked site
 * data) fails open — never throws, and treats "can't tell if we've already
 * opened it" as "go ahead and open it".
 */
export function shouldAutoOpenForRun(
  runId: string,
  agentStatus: ReleaseAgentStatus | undefined,
  storage: KeyValueStorage | null
): boolean {
  if (!isLiveAgentStatus(agentStatus)) return false;
  if (!storage) return true;
  try {
    return storage.getItem(`${RELEASE_AGENT_AUTO_OPEN_KEY}:${runId}`) === null;
  } catch {
    return true;
  }
}

/** Marks `runId` as auto-opened so `shouldAutoOpenForRun` won't fire again this session. Never throws. */
export function markAutoOpened(runId: string, storage: KeyValueStorage | null): void {
  if (!storage) return;
  try {
    storage.setItem(`${RELEASE_AGENT_AUTO_OPEN_KEY}:${runId}`, '1');
  } catch {
    // Best-effort — see `writeSessionPin` in useAssistantChat.ts for the same pattern.
  }
}

/**
 * The kickoff request for auto-opening the assistant onto a run's live
 * agent. The text is deliberately phrased as an observation request, never
 * an affirmative ("ok"/"yes"/"go") — this message is sent on the user's
 * behalf without them having typed it, and the agent may be holding on a
 * promotion approval question; an affirmative-reading kickoff could be
 * misread as answering it.
 */
export function buildReleaseAgentKickoff(runId: string): KickoffRequest {
  return {
    text: `Watch the release agent for release run ${runId}: tell me what it is doing, what it is waiting on, and any question it has for me.`,
    label: 'Watching the release agent',
    params: { runId },
    metadata: { source: 'release-agent-watch', runId },
  };
}
